/**
 * Highlight — fremhever sokeordet i en tekst med en gul markor.
 *
 * Brukes pa lister/tabeller med sokefelt for a gi visuell tilbakemelding pa
 * hvilken del av raden som matchet. Splitter teksten rundt forekomster av
 * `query` (case-insensitive, locale-aware) og pakker treffene i `<mark>`.
 *
 * Returnerer ren tekst nar `query` er tom eller ingen match finnes.
 */

import { Fragment, type ReactNode } from "react";
import { cn } from "../../lib/cn";

export function highlightMatch(text: string, query: string): ReactNode {
  const q = query.trim();
  if (!q) return text;
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  if (parts.length === 1) return text;
  const lowerQ = q.toLocaleLowerCase("nb-NO");
  return parts.map((part, i) => {
    const key = `${i}-${part}`;
    return part.toLocaleLowerCase("nb-NO") === lowerQ ? (
      <mark key={key} className="bg-yellow-200 text-inherit dark:bg-yellow-500/40">
        {part}
      </mark>
    ) : (
      <Fragment key={key}>{part}</Fragment>
    );
  });
}

export interface HighlightProps {
  /** Teksten som rendres med treff fremhevet. */
  text: string;
  /** Sokeordet. Tom string = ingen highlighting. */
  query: string;
  className?: string;
}

/** Render-komponent som wrapper {@link highlightMatch} i en `<span>`. */
export function Highlight({ text, query, className }: HighlightProps) {
  return <span className={cn(className)}>{highlightMatch(text, query)}</span>;
}
